import {select, call, takeLatest} from 'redux-saga/effects';

import {setPrescription} from 'features/wizardMs/reducers/prescription/prescription/prescription';
import {toggleOption} from 'features/wizardMs/reducers/customerConfiguration/customerConfiguration';
import {getActiveUsageSku} from 'features/wizardMs/reducers/usages/usages';
import {ActionType} from 'constants/flow/flowTypes';
import {DISTANCE, READING} from 'features/wizardMs/constants/usages';
import {getPrescription} from 'features/wizardMs/selectors/prescription/prescription';
import {MetaOptionsType} from 'features/wizardMs/reducers/customerConfiguration/customerConfigurationType';
import {
    ALERT_READING_VALUE,
    ALERT_SINGLEVISION_VALUE,
    ALERT_SAME_VALUE,
} from 'constants/wizard/alerts';
import {FILL_IT} from 'constants/wizard/prescriptionType';

/**
 * Show alert for single vision by condition
 * @param {string} name - Field name
 * @param {string} value - Field value
 */
export function* showAlertForSingleVision(name: string, value: string) {
    const activeUsageSku = yield select(getActiveUsageSku);
    if (activeUsageSku === DISTANCE && name.indexOf('sph') !== -1 && Number(value) > 0)
        yield call(alert, ALERT_SINGLEVISION_VALUE);
}

/**
 * Show alert for near vision by condition
 * @param {string} name - Field name
 * @param {string} value - Field value
 */
export function* showAlertForNearVision(name: string, value: string) {
    const activeUsageSku = yield select(getActiveUsageSku);
    if (activeUsageSku === READING && name.indexOf('sph') !== -1 && Number(value) < 0)
        yield call(alert, ALERT_READING_VALUE);
}

/**
 * Show alert for product wizard by condition
 * @param {ActionType<updateScheme>} action - Last changed prescription value
 */
export function* showAlert(
    action: ActionType<{name: string; value: string}, {field: string; value: string}>,
) {
    if (!action.meta) return;
    const {field, value} = action.meta;
    const {
        values: {od, os},
    }: ReturnType<typeof getPrescription> = yield select(getPrescription);

    yield call(showAlertForSingleVision, field, value);
    yield call(showAlertForNearVision, field, value);

    if (
        ['cyl', 'axi'].includes(field) &&
        (od[field] > 0 || od[field] < 0) &&
        Number(os[field]) === Number(od[field])
    )
        yield call(alert, ALERT_SAME_VALUE);
}

/**
 * Check and call saga showSpecificPopup
 * @param {string} html - html for popup
 */
export function* callShowSpecificPopup(html: string) {
    const {
        values: {method, id: indexPrescription},
    }: ReturnType<typeof getPrescription> = yield select(getPrescription);

    const isShowAlert = method === FILL_IT && indexPrescription >= 0;
    if (isShowAlert) yield call(alert, html);
}

/**
 * Handler for show alert after change usage
 */
export function* showAlertByUsage({
    meta = {type: ''},
}: ActionType<{parentNodeId?: number; node: number}, MetaOptionsType>) {
    if (meta.type !== 'usage') return;
    const activeUsageSku = yield select(getActiveUsageSku);
    const {
        values: {os, od},
    }: ReturnType<typeof getPrescription> = yield select(getPrescription);
    if (activeUsageSku === DISTANCE && (Number(od.sph) > 0 || Number(os.sph) > 0)) {
        yield call(callShowSpecificPopup, ALERT_SINGLEVISION_VALUE);
    }

    if (activeUsageSku === READING && (Number(od.sph) < 0 || Number(os.sph) < 0)) {
        yield call(callShowSpecificPopup, ALERT_READING_VALUE);
    }
}

/**
 * Root
 */
export function* showAlertsForPrescriptionSaga() {
    yield takeLatest(String(setPrescription), showAlert);
    yield takeLatest(toggleOption, showAlertByUsage);
}
